"use client";

import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Loader2, Shield, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailFromUrl = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/reset-password', {
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      setDone(true);
      toast.success('Password reset successfully!');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to reset password.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] bg-indigo-950 overflow-hidden flex-col p-12 sticky top-0 h-screen">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=2070&auto=format&fit=crop')",
          backgroundSize: 'cover', backgroundPosition: 'center',
        }} />
        <div className="relative z-10 mb-auto">
          <div className="flex flex-col">
            <img src="/assets/logo/logo.svg" className="h-10 w-auto object-contain brightness-0 invert" alt="ShoplyCircle Logo" />
            <span className="text-[10px] uppercase tracking-widest text-indigo-300 font-extrabold mt-2 ml-0.5 leading-none">Manufacturer Portal</span>
          </div>
        </div>
        <div className="relative z-10 space-y-8 mt-auto">
          <div>
            <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">Create New Password</h1>
            <p className="text-indigo-100/50 leading-relaxed text-lg">Choose a strong password you haven't used before for your account.</p>
          </div>
        </div>
        <div className="relative z-10 mt-auto pt-8 border-t border-indigo-500/20">
          <p className="text-xs text-indigo-100/40">Secure password reset with encrypted links</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-gray-50/50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #6366f1 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div className="w-full max-w-md relative z-10">
          <div className="lg:hidden flex flex-col items-center gap-1.5 mb-10 justify-center">
            <img src="/assets/logo/logo.svg" className="h-9 w-auto object-contain" alt="ShoplyCircle Logo" />
            <span className="text-[10px] uppercase tracking-widest text-indigo-600 font-extrabold leading-none">Manufacturer Portal</span>
          </div>
          <Card className="w-full border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pb-4 px-6 pt-8">
              <CardTitle className="text-2xl font-bold text-gray-900">Reset Password</CardTitle>
              <p className="text-gray-500">Enter your new password</p>
            </CardHeader>
            <CardContent className="px-6 pb-8">
              {done ? (
                <div className="text-center space-y-4 py-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-indigo-600" />
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">Password reset complete</p>
                  <p className="text-gray-500 text-sm">Your password has been updated successfully.</p>
                  <Button onClick={() => navigate('/login')}
                    className="mt-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold rounded-2xl px-8"
                  >
                    Sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-bold text-gray-700 ml-1">Email Address</label>
                    <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                      <Input id="email" type="email" required autoComplete="email" readOnly={!!emailFromUrl}
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="pl-12 h-13 py-3 rounded-xl bg-gray-50 border-gray-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 transition-all shadow-sm hover:shadow-md"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-bold text-gray-700 ml-1">New Password</label>
                    <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                      <Input id="password" type="password" required autoComplete="new-password"
                        placeholder="Min. 8 characters"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="pl-12 h-13 py-3 rounded-xl bg-gray-50 border-gray-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 transition-all shadow-sm hover:shadow-md"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="password_confirmation" className="text-sm font-bold text-gray-700 ml-1">Confirm Password</label>
                    <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                      <Input id="password_confirmation" type="password" required autoComplete="new-password"
                        placeholder="Repeat password"
                        value={passwordConfirmation}
                        onChange={e => setPasswordConfirmation(e.target.value)}
                        className="pl-12 h-13 py-3 rounded-xl bg-gray-50 border-gray-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 transition-all shadow-sm hover:shadow-md"
                      />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold text-lg shadow-xl shadow-indigo-600/30 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] active:scale-100 flex items-center justify-center gap-2"
                  >
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Resetting...</> : 'Reset Password'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
          <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-400">
            <Shield className="h-4 w-4 text-indigo-400" />
            <span>Secure & encrypted authentication</span>
          </div>
        </div>
      </div>
    </div>
  );
}
